import * as React from 'react';
import * as moment from "moment";
import { Row, Col, Button } from 'antd';
import { observer } from 'mobx-react';
import { connect } from "react-redux";

import { DataTableView, Column, ToolbarVisibility } from "src/core/ui/data-table.view";
import { BooleanView } from 'src/core/ui/boolean.view';
import { TimePickerView, DisableKind } from 'src/core/ui/timepicker.view';
import { TextView } from 'src/core/ui/text.view';
import { MapDialogView } from 'src/core/ui/map-dialog.view';
import { ProductEditorController } from 'src/app/products/products/parts/product-editor.controller';
import { ProductServiceTypeEditorController } from 'src/app/products/product-service-types/parts/product-service-type-editor.controller';
import { ProductFilterModalController } from 'src/app/products/products/form/parts/product-filter-modal.controller';
import { ProductProviderEditorController } from 'src/app/products/product-providers/parts/product-provider-editor.controller';
import { SupplierEditorController } from 'src/app/products/suppliers/parts/supplier-editor.controller';

interface PropsFromState {
	currencies: CurrencySummary[];
}

interface TourProductDataSet extends DataSet<TourItineraryProductItem> {
	updateOrders: () => void;
}

interface State {
	showDialog: boolean;
	product?: TourItineraryProductItem;
}

interface Props extends ComponentViewProps<TourProductDataSet> {
	product?: ProductItem;
	itinerary: TourItineraryItem;
	mode: ControlMode;
	createNew: () => any;
	defaultLanguage: string;

	readOnly?: boolean;
	fromDate?: Date;
}

@observer
class TourItineraryProductView extends React.Component<Props & PropsFromState, State> {
	public state = { showDialog: false, product: null };

	private columns: Column[] = [
		{
			key: 'productId',
			dataIndex: 'productId',
			title: 'Product',
			editable: (text: string, record: TourItineraryProductItem, index: number, isSelected: boolean) => {
				const { defaultLanguage, setBusy } = this.props;
				if (isSelected) {
					return (
						<ProductEditorController
							label={null}
							mode="edit"
							setBusy={setBusy}
							value={record.productId}
							baseProductDisable={true}
							onChanged={this.onProductChange(record)}
						>
							<Button
								type="primary"
								style={{ minWidth: 'auto', width: '40px' }}
								onClick={() => {
									this.setState({
										showDialog: true,
										product: record
									})

								}}
								disabled={this.props.mode == 'display'}
								icon={"search"}
							/>
						</ProductEditorController>
					);
				}
				return record.dynamicProperties.displayName[defaultLanguage] || "";
			},
		},
		{
			key: 'productServiceTypeDisplayName',
			dataIndex: 'productServiceTypeDisplayName',
			title: 'Service',
			editable: (text: string, record: TourItineraryProductItem, index: number, isSelected: boolean) => {
				const { setBusy } = this.props;
				if (isSelected) {
					if (record.productCategoryId) {
						return (
							<ProductServiceTypeEditorController
								label={null}
								mode="edit"
								setBusy={setBusy}
								value={record.productServiceTypeId}
								onChanged={this.onServiceChange(record)}
								categoryId={record.productCategoryId}
							/>
						);
					} else {
						return "Please select a product";
					}
				}
				return record.productServiceTypeDisplayName || "";
			},
			width: 200,
		},
		{
			key: 'productProviderName',
			dataIndex: 'productProviderName',
			title: 'Provider',
			editable: (text: string, record: TourItineraryProductItem, index: number, isSelected: boolean) => {
				const { setBusy } = this.props;
				if (isSelected) {
					if (record.productCategoryId) {
						return (
							<ProductProviderEditorController
								label={null}
								mode="edit"
								onChange={this.onProviderChange(record)}
								value={record.productProviderId}
								setBusy={setBusy}
								productType={record.productType}
							/>
						);
					} else {
						return "Please select a product";
					}
				}
				return text || "";
			},
			width: 200,
		},
		{
			key: 'supplierTitle',
			dataIndex: 'supplierTitle',
			title: 'Supplier',
			editable: (text: string, record: TourItineraryProductItem, index: number, isSelected: boolean) => {
				const { setBusy } = this.props;
				if (isSelected) {
					if (record.productCategoryId) {
						return (
							<SupplierEditorController
								label={null}
								mode="edit"
								supplierType={record.productType == 2 ? 1 : 0}
								onChange={this.onSupplierChange(record)}
								value={record.supplierId}
								setBusy={setBusy}
								productId={record.productId}
							/>
						);
					} else {
						return "Please select a product";
					}
				}
				return text || "";
			},
			width: 200,
		},
		{
			key: 'duration',
			title: 'Duration (Day - Hour)',
			children: [
				{
					key: 'dateFrom',
					dataIndex: 'dateFrom',
					title: 'From',
					editable: (text: string, record: TourItineraryProductItem, index: number, isSelected: boolean) => {
						const items = this.props.model.items.slice();

						const firstDate = moment.utc(this.props.itinerary.entity.createdOn).hour(0).minute(0).second(0);

						const date = moment.utc(record.dateFrom);


						const diff = date.diff(firstDate, "d") + 1;
						const hour = date.format("HH:mm");

						if (isSelected) {
							return (
								<Row gutter={0}>
									<Col xs={12}>
										<TextView
											label={null}
											mode="edit"
											onChanged={this.onDurationChange(record, "dateFrom")}
											required={true}
											value={date.diff(firstDate, "d") + 1}
										/>
									</Col>
									<Col xs={12}>
										<TimePickerView
											dateUsage="utc"
											disableKind={DisableKind.disableSeconds}
											label={null}
											mode="edit"
											onChanged={this.onDateChange(record, "dateFrom")}
											required={true}
											value={record.dateFrom}
										/>
									</Col>
								</Row>
							);
						}

						return this.props.fromDate ? `${moment(this.props.fromDate).add(diff, 'd').format("DD/MM/YYYY")} - ${hour}` : `${diff} - ${hour}`;
					},
					width: 150,
				},
				{
					key: 'dateTo',
					dataIndex: 'dateTo',
					title: 'To',
					editable: (text: string, record: TourItineraryProductItem, index: number, isSelected: boolean) => {
						const items = this.props.model.items.slice();
						const firstDate = moment.utc(this.props.itinerary.entity.createdOn).hour(0).minute(0).second(0);
						const date = moment.utc(record.dateTo);

						const diff = date.diff(firstDate, "d") + 1;
						const hour = date.format("HH:mm");

						if (isSelected) {
							return (
								<Row gutter={0}>
									<Col xs={12}>
										<TextView
											label={null}
											mode="edit"
											onChanged={this.onDurationChange(record, "dateTo")}
											required={true}
											value={date.diff(firstDate, "d") + 1}
										/>
									</Col>
									<Col xs={12}>
										<TimePickerView
											dateUsage="utc"
											disableKind={DisableKind.disableSeconds}
											label={null}
											mode="edit"
											onChanged={this.onDateChange(record, "dateTo")}
											required={true}
											value={record.dateTo}
										/>
									</Col>
								</Row>
							);
						}
						return this.props.fromDate ? `${moment(this.props.fromDate).add(diff, 'd').format("DD/MM/YYYY")} - ${hour}` : `${diff} - ${hour}`;
					},
					width: 150,
				},
			],
		},
		{
			key: 'isRequired',
			dataIndex: 'isRequired',
			title: 'Required',
			editable: (text: string, record: TourItineraryProductItem, index: number, isSelected: boolean) => {
				return (
					<BooleanView
						label={null}
						mode={this.props.mode}
						onChanged={this.onRequiredChange(record)}
						required={true}
						size="small"
						value={record.isRequired}
					/>
				);
			},
			width: 100,
		},
		{
			key: 'location',
			dataIndex: 'location',
			title: 'Location',
			render: (text: string, record: TourItineraryProductItem) => {
				return (
					<MapDialogView
						location={record.location}
						mode={this.props.mode}
						onChangeLocation={e => record.location = e}
						zoom={14}
						withoutText={true}
					/>
				);
			},
			width: 100,
		}
	];

	public constructor(props) {
		super(props);
	}

	public render() {
		const { createNew, mode, model, setBusy } = this.props;
		if (model == null) {
			return null;
		}

		let visibility = ToolbarVisibility.hideSearch;

		if (this.props.readOnly)
			visibility |= ToolbarVisibility.hideModifyButtons;

		return (
			<React.Fragment>
				<ProductFilterModalController
					showModal={this.state.showDialog}
					closeDialog={this.closeDialog}
					model={this.state.product}
					onProductChange={this.onProductChange}
					setBusy={this.props.setBusy} />
				<DataTableView
					mode={mode}
					setBusy={setBusy}
					columns={this.columns}
					model={model}
					createNewItem={createNew}
					hideToolbar={visibility}
				/>
			</React.Fragment>
		);
	}

	private onProductChange = (record: TourItineraryProductItem) => (item: ProductSummary) => {
		const { defaultLanguage } = this.props;
		record.productId = item.id;
		record.productCategoryId = item.categoryId;
		record.dynamicProperties.displayName[defaultLanguage] = (item.parentDisplayName ? item.parentDisplayName + "-" : "") + item.displayName;
		record.location = item.location;
		record.productType = item.productType;
		record.productServiceTypeId = undefined;
		record.productServiceTypeDisplayName = undefined;
		this.setState({ product: record });
	}

	private onServiceChange = (record: TourItineraryProductItem) => (item: ProductServiceTypeSummary) => {
		record.productServiceTypeId = item.id;
		record.productServiceTypeDisplayName = item.displayName;
	}

	private onProviderChange = (record: TourItineraryProductItem) => (item: ProductProviderItem) => {
		record.productProviderId = item.id;
		record.productProviderApiName = item.apiName;
		record.productProviderName = item.name;
	}

	private onSupplierChange = (record: TourItineraryProductItem) => (item: SupplierSummary) => {
		record.supplierId = item.id;
		record.supplierTitle = item.title;
		const currency = this.props.currencies.find(e => e.id === item.defaultCurrencyId);
		record.supplierDefaultCurrencyIso3Name = currency.iso3Name;
	}

	private onRequiredChange = (record: TourItineraryProductItem) => (val: boolean) => record.isRequired = val;

	private onDateChange = (record: TourItineraryProductItem, field: keyof TourItineraryProductItem) => (val: Date) => {
		const date = moment.utc(val);
		record[field] = moment.utc(record[field]).hour(date.hour()).minute(date.minute()).second(0).toDate();
		this.props.model.updateOrders();
	}

	private onDurationChange = (record: TourItineraryProductItem, field: keyof TourItineraryProductItem) => (val: string) => {
		const firstDate = moment.utc(this.props.itinerary.entity.createdOn).hour(0).minute(0).second(0);
		const duration = parseInt(val);
		const oldDuration = moment.utc(record[field]).diff(firstDate, "day") + 1;
		record[field] = moment.utc(record[field]).add(duration - oldDuration, "day").toDate();
		this.props.model.updateOrders();
	}

	private closeDialog = (ev?: React.MouseEvent<HTMLButtonElement>) => {
		this.setState({
			showDialog: false,
			product: null
		})
	};
}

const mapStateToProps = (state: IApplicationState): PropsFromState => {
	return {
		currencies: state.currencies,
	};
};

const connected = connect(
	mapStateToProps,
)(TourItineraryProductView);
export { connected as TourItineraryProductView };



// WEBPACK FOOTER //
// ./src/app/products/tour-itineraries/form/parts/tour-itinerary-products.view.tsx